import React, { useEffect, useState } from 'react';
import { TableRowLayout } from './TableRowLayout';
import { Name } from './Name';
import { Jobs } from './Jobs';
import { shortFormatDate } from '../../../../../../../utils/timeFormat';
import Stages from './Stages';
import { PendingActions } from './PendingActions';
import { Dropdown } from '../../../../../../../components/Dropdown';
import {
  appendRedirectUri,
  removeTrailingSlashes,
} from '../../../../../../../utils/url';
import { IconSpan } from '../../../../../../../components/IconSpan';
import { ArchiveCandidate } from './ArchiveCandidate';
import classNames from 'classnames';
import { Channel } from './Channel';
import { CheckboxButton } from '../../../../../../../components/CheckboxButton';
import { DeleteCandidate } from './DeleteCandidate';
import { User } from '../../../../../../../entities/User';
import { HrisEmployee } from '../../../../../../../entities/applicant_tracking/HrisEmployees';
import { HrisProvider } from '../../../../../../../enums/IntegrationProvider/HrisProvider';
import { renderPushToHris } from '../../../../../../CandidatePage/CandidateActions/DropdownActions';
import { AlertObject } from '../../../../../../../components/Alert';
import { EmailAccountIntegration } from '../../../../../../../entities/EmailAccountIntegration';
import { NewListCandidate } from '../../../../../../../entities/v1/applicant_tracking/NewListCandidate';
import { ApplicationService } from '../../../../../../../services/v1/applicant_tracking/ApplicationService';
import { SendOfferSwitch } from '../../../../../SendOfferSwitch';
import {
  sendOfferButtonIcon,
  sendOfferButtonText,
} from '../../../../../../../utils/applicant_tracking/offers';
import { ApplicationStatus } from '../../../../../../../entities/applicant_tracking/PipelineApplication';

interface PropTypes {
  atsSignableOfferEnabled: boolean;
  offerRequisitionFlowEnabled: boolean;
  candidates: NewListCandidate[];
  currentUser: User;
  emailAccountIntegration: EmailAccountIntegration;
  hrisEmployees?: HrisEmployee[];
  hrisProvider?: HrisProvider;
  selectedCandidates: NewListCandidate[];
  bulkSelectedIds: number[] | null;
  onBulkSelectionRemove: (ids: number[]) => void;
  onBulkSelectionAdd: (ids: number[]) => void;
  setSelectedCandidates: (candidates: NewListCandidate[]) => void;
  reloadPage: () => void;
  setAlert: (alert: AlertObject) => void;
  candidateForAction: NewListCandidate;
  setCandidateForAction: (candidates: NewListCandidate) => void;
  setPushToHrisModalActive: (active: boolean) => void;
  setIsEmailModalOpen: (open: boolean) => void;
  setIsRejectModalOpen: (open: boolean) => void;
}

interface RowActionPropTypes {
  currentUser: User;
  candidate: NewListCandidate;
  hrisEmployee?: HrisEmployee;
  hrisProvider?: HrisProvider;
  anonymized: boolean;
  offerRequisitionFlowEnabled: boolean;
  onClickArchive: () => void;
  onClickDelete: () => void;
  onClickPushToHris: () => void;
  onClickSendOffer: () => void;
  setIsEmailModalOpen: (open: boolean) => void;
  setIsRejectModalOpen: (open: boolean) => void;
}

interface CandidateRowPropTypes {
  candidate: NewListCandidate;
  hrisEmployee?: HrisEmployee;
  hrisProvider?: HrisProvider;
  currentUser: User;
  checked: boolean;
  selectedCandidates: NewListCandidate[];
  bulkSelectedIds: number[] | null;
  onBulkSelectionRemove: (ids: number[]) => void;
  onBulkSelectionAdd: (ids: number[]) => void;
  offerRequisitionFlowEnabled: boolean;
  setSelectedCandidates: (candidates: NewListCandidate[]) => void;
  setCandidateForAction: (candidate: NewListCandidate) => void;
  setArchiveModalActive: (active: boolean) => void;
  setDeleteModalActive: (active: boolean) => void;
  setPushToHrisModalActive: (active: boolean) => void;
  setSendOfferModalActive: (active: boolean) => void;
  setIsEmailModalOpen: (open: boolean) => void;
  setIsRejectModalOpen: (open: boolean) => void;
}

function menuAction(
  action: () => void,
  text: string,
  iconName: string,
  disabled = false,
) {
  return {
    action: action,
    buttonChild: (
      <IconSpan
        spanText={text}
        icon={{ name: iconName, className: 'fs-4' }}
        className={classNames(disabled ? 'text-muted' : 'text-blue')}
      />
    ),
    isDisabled: disabled,
  };
}

function getCandidateUrl(candidate: NewListCandidate) {
  if (candidate.application == null) return '#';

  return appendRedirectUri(
    `${removeTrailingSlashes(window.location.origin)}/applications/${
      candidate.application.id
    }`,
  );
}

function editCandidateProfile(
  editCandidatePermitted: boolean,
  candidate: NewListCandidate,
) {
  if (!editCandidatePermitted || candidate.redacted) return null;

  const url = appendRedirectUri(
    `/applicant_tracking/candidates/${candidate.id}/edit`,
  );

  return menuAction(
    () => (window.location.href = url.toString()),
    'Edit Candidate Profile',
    'bi-person',
  );
}

function addJobApplicationAction(
  newApplicationPermitted: boolean,
  candidate: NewListCandidate,
) {
  if (!newApplicationPermitted || candidate.redacted) return null;

  const url = new URL(
    `/applicant_tracking/applications/new`,
    window.location.origin,
  );
  url.searchParams.set('candidate_id', candidate.id.toString());

  return menuAction(
    () => (window.location.href = appendRedirectUri(url.toString())),
    'Add Job Application',
    'bi-plus-circle',
  );
}

function editJobApplicationAction(
  editApplicationPermitted: boolean,
  candidate: NewListCandidate,
) {
  if (!candidate.application || !editApplicationPermitted || candidate.redacted)
    return null;

  const url = `/applicant_tracking/applications/${candidate.application.id}/edit`;

  return menuAction(
    () => (window.location.href = appendRedirectUri(url.toString())),
    'Edit Job Application',
    'bi-pencil',
  );
}

function deleteApplicationAction(
  candidate: NewListCandidate,
  currentUser: User,
  onClickDelete: () => void,
) {
  if (candidate.status === 'active' || !currentUser.isAdmin) return null;
  if (candidate.application != null) return null;

  return menuAction(() => onClickDelete(), 'Delete', 'bi-trash');
}

function sendOfferAction(
  onClickSendOffer: () => void,
  offerRequisitionFlowEnabled: boolean,
  enabled?: boolean,
) {
  if (!enabled) return null;

  return menuAction(
    () => onClickSendOffer(),
    sendOfferButtonText(offerRequisitionFlowEnabled),
    sendOfferButtonIcon(offerRequisitionFlowEnabled),
  );
}

function viewOfferRequisition(offerRequisitionId: number) {
  if (!offerRequisitionId) return null;

  return menuAction(
    () =>
      (window.location.href = `/applicant_tracking/offer_requisitions/${offerRequisitionId}`),
    'View Offer Requisition',
    'bi-file-medical',
  );
}

function archiveApplicationAction(
  onClickArchive: () => void,
  archivable?: boolean,
) {
  if (!archivable) return null;

  return menuAction(() => onClickArchive(), 'Archive', 'bi-archive');
}

function unarchiveApplicationAction(
  onClickArchive: () => void,
  unarchivable?: boolean,
) {
  if (!unarchivable) return null;

  return menuAction(() => onClickArchive(), 'Unarchive', 'bi-archive');
}

function renderRejectCandidate(
  onClick: () => void,
  editApplicationPermitted?: boolean,
  applicationStatus?: string,
) {
  if (
    !editApplicationPermitted ||
    applicationStatus == ApplicationStatus.Rejected
  ) {
    return null;
  }

  return menuAction(() => onClick(), 'Reject Candidate', 'bi-file-x');
}

function RowActions(props: RowActionPropTypes) {
  const [menuActions, setMenuActions] = useState(null);

  useEffect(() => {
    (async () => {
      const actions = await ApplicationService.listApplicationActions({
        applicationId: props.candidate.application?.id,
        candidateId: props.candidate.id,
      });

      if (props.anonymized) {
        return setMenuActions(
          [
            archiveApplicationAction(props.onClickArchive, actions?.archivable),
            unarchiveApplicationAction(
              props.onClickArchive,
              actions?.unarchivable,
            ),
          ].filter(Boolean),
        );
      }

      setMenuActions(
        [
          menuAction(
            () => (window.location.href = getCandidateUrl(props.candidate)),
            'View',
            'bi-eye',
            props.candidate.application == null,
          ),
          editCandidateProfile(actions.editCandidatePermitted, props.candidate),
          addJobApplicationAction(
            actions.newApplicationPermitted,
            props.candidate,
          ),
          editJobApplicationAction(
            actions.editApplicationPermitted,
            props.candidate,
          ),
          renderRejectCandidate(
            () => props.setIsRejectModalOpen(true),
            actions.editApplicationPermitted,
            props.candidate.application?.status,
          ),
          archiveApplicationAction(props.onClickArchive, actions?.archivable),
          unarchiveApplicationAction(
            props.onClickArchive,
            actions?.unarchivable,
          ),
          renderPushToHris(
            () => props.onClickPushToHris(),
            props.candidate.redacted,
            props.candidate?.application?.status,
            props.hrisEmployee?.externalUrl,
            actions.pushCandidateToHrisPermitted ? props.hrisProvider : null,
          ),
          deleteApplicationAction(
            props.candidate,
            props.currentUser,
            props.onClickDelete,
          ),
          sendOfferAction(
            props.onClickSendOffer,
            props.offerRequisitionFlowEnabled,
            actions.sendOfferPermitted && !props.candidate.redacted,
          ),
          viewOfferRequisition(props.candidate.application?.offerRequisitionId),
        ].filter(Boolean),
      );
    })();
  }, []);

  return (
    <Dropdown
      buttonIcon={{
        name: 'bi-three-dots',
        className: 'fs-5 text-blue',
      }}
      isLoading={menuActions == null}
      menuActions={menuActions || []}
    />
  );
}

function getUpdatedAtString(
  applicationUpdatedAt?: Date,
  candidateUpdatedAt?: Date,
): string {
  if (applicationUpdatedAt == null && candidateUpdatedAt == null) {
    return 'N/A';
  }

  if (!applicationUpdatedAt || candidateUpdatedAt > applicationUpdatedAt) {
    return shortFormatDate(candidateUpdatedAt);
  }

  return shortFormatDate(applicationUpdatedAt);
}

function isCandidateSelected(
  selectedCandidates: NewListCandidate[],
  candidate: NewListCandidate,
  bulkSelectedIds: number[] | null,
): boolean {
  if (bulkSelectedIds !== null && candidate.application?.id != null) {
    return bulkSelectedIds.includes(candidate.application.id);
  }
  return (
    selectedCandidates.find(
      (c) =>
        c.id === candidate.id &&
        c.application?.id === candidate.application?.id,
    ) != null
  );
}

function handleCheckCandidate(
  selectedCandidates: NewListCandidate[],
  candidate: NewListCandidate,
  checked: boolean,
  setSelectedCandidates: (selectedCandidates: NewListCandidate[]) => void,
  bulkSelectedIds: number[] | null,
  onBulkSelectionRemove: (ids: number[]) => void,
  onBulkSelectionAdd: (ids: number[]) => void,
) {
  if (bulkSelectedIds !== null && candidate.application?.id != null) {
    if (checked) {
      onBulkSelectionAdd([candidate.application.id]);
    } else {
      onBulkSelectionRemove([candidate.application.id]);
    }
    return;
  }
  if (checked) {
    if (!isCandidateSelected(selectedCandidates, candidate, null)) {
      const combinedList = [...selectedCandidates, candidate];

      setSelectedCandidates(combinedList);
    }
  } else {
    setSelectedCandidates(
      selectedCandidates.filter(
        (c) =>
          c.id !== candidate.id ||
          c.application?.id !== candidate.application?.id,
      ),
    );
  }
}

function CandidateRow(props: CandidateRowPropTypes) {
  const bulkSelectionEnabled =
    props.candidate.anonymized === false &&
    props.candidate.status !== 'draft' &&
    props.candidate.application?.bulkActionsPermitted;

  return (
    <TableRowLayout classNames={props.checked && 'bg-light-info'}>
      <CheckboxButton
        checked={props.checked}
        onChange={() =>
          handleCheckCandidate(
            props.selectedCandidates,
            props.candidate,
            !props.checked,
            props.setSelectedCandidates,
            props.bulkSelectedIds,
            props.onBulkSelectionRemove,
            props.onBulkSelectionAdd,
          )
        }
        name={null}
        disabled={!bulkSelectionEnabled}
      />
      <Name
        candidate={props.candidate}
        hrisEmployee={props.hrisEmployee}
        application={props.candidate.application}
      />
      <Jobs candidate={props.candidate} />
      <span>
        {getUpdatedAtString(
          props.candidate.application?.updatedAt,
          props.candidate.updatedAt,
        )}
      </span>
      <Channel application={props.candidate.application} />
      <Stages
        stageName={props.candidate.application?.currentStage?.name}
        millisInStage={props.candidate.application?.currentStage?.millisInStage}
      />
      <PendingActions application={props.candidate.application} />
      <RowActions
        candidate={props.candidate}
        hrisEmployee={props.hrisEmployee}
        hrisProvider={props.hrisProvider}
        currentUser={props.currentUser}
        anonymized={props.candidate.anonymized}
        offerRequisitionFlowEnabled={props.offerRequisitionFlowEnabled}
        onClickArchive={() => {
          props.setCandidateForAction(props.candidate);
          props.setArchiveModalActive(true);
        }}
        onClickDelete={() => {
          props.setCandidateForAction(props.candidate);
          props.setDeleteModalActive(true);
        }}
        onClickPushToHris={() => {
          props.setCandidateForAction(props.candidate);
          props.setPushToHrisModalActive(true);
        }}
        onClickSendOffer={() => {
          props.setCandidateForAction(props.candidate);
          props.setSendOfferModalActive(true);
        }}
        setIsEmailModalOpen={() => {
          props.setCandidateForAction(props.candidate);
          props.setIsEmailModalOpen(true);
        }}
        setIsRejectModalOpen={() => {
          props.setCandidateForAction(props.candidate);
          props.setIsRejectModalOpen(true);
        }}
      />
    </TableRowLayout>
  );
}

export function TableBody(props: PropTypes) {
  const [archiveModalActive, setArchiveModalActive] = useState(false);
  const [deleteModalActive, setDeleteModalActive] = useState(false);
  const [sendOfferModalActive, setSendOfferModalActive] = useState(false);

  const handleSendOfferClose = () => {
    setSendOfferModalActive(false);
    props.reloadPage();
  };

  return (
    <>
      <ArchiveCandidate
        archiveModalActive={archiveModalActive}
        candidate={props.candidateForAction}
        onClose={() => setArchiveModalActive(false)}
      />
      <DeleteCandidate
        deleteModalActive={deleteModalActive}
        candidate={props.candidateForAction}
        onClose={() => setDeleteModalActive(false)}
      />
      <SendOfferSwitch
        applicationId={props.candidateForAction?.application?.id}
        atsSignableOfferEnabled={props.atsSignableOfferEnabled}
        isOpen={sendOfferModalActive}
        offerRequisitionFlowEnabled={props.offerRequisitionFlowEnabled}
        to={props.candidateForAction?.email}
        onClose={handleSendOfferClose}
        setAlert={props.setAlert}
        hasEmailConnected={props.currentUser.hasEmailConnected || false}
      />
      <tbody>
        {props.candidates.map((candidate: NewListCandidate, idx: number) => {
          return (
            <CandidateRow
              key={idx}
              candidate={candidate}
              hrisEmployee={props.hrisEmployees?.find(
                (e) => e.applicationId === candidate.application?.id,
              )}
              hrisProvider={props.hrisProvider}
              currentUser={props.currentUser}
              checked={isCandidateSelected(
                props.selectedCandidates,
                candidate,
                props.bulkSelectedIds,
              )}
              selectedCandidates={props.selectedCandidates}
              bulkSelectedIds={props.bulkSelectedIds}
              onBulkSelectionRemove={props.onBulkSelectionRemove}
              onBulkSelectionAdd={props.onBulkSelectionAdd}
              offerRequisitionFlowEnabled={props.offerRequisitionFlowEnabled}
              setArchiveModalActive={setArchiveModalActive}
              setCandidateForAction={props.setCandidateForAction}
              setDeleteModalActive={setDeleteModalActive}
              setSelectedCandidates={props.setSelectedCandidates}
              setPushToHrisModalActive={props.setPushToHrisModalActive}
              setSendOfferModalActive={setSendOfferModalActive}
              setIsEmailModalOpen={props.setIsEmailModalOpen}
              setIsRejectModalOpen={props.setIsRejectModalOpen}
            />
          );
        })}
      </tbody>
    </>
  );
}
